"use client";

/**
 * /login — the new landing's sign-in (spec docs/superpowers/specs/2026-10-17-waitlist-front-door-design.md),
 * ported from vestra-v3 pages/onboarding.html's sign-in view: the clay team on a rooftop on the left with
 * Bash saying hello and your seat kept, the sign-in on the right. One button, real Google: the auth
 * callback sends existing users home and everyone else to the waitlist.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ClayPicture } from "./ClayPicture";
import { Arrow, InkDefs, Mug, Pop, Spark, Sun, Underline } from "./Doodles";

const DIDNT_START = "Google sign-in didn’t start. Try again.";

/** Bash's hello, by the visitor's own clock. */
function greeting(hour: number): string {
  if (hour >= 5 && hour < 12) return "Morning! Coffee’s on.";
  if (hour >= 12 && hour < 17) return "Afternoon! Pull up a chair.";
  if (hour >= 17 && hour < 22) return "Evening! Lights are still on.";
  return "Up late? So are we.";
}

export function SignIn() {
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hello, setHello] = useState(greeting(9));
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    // The landing's solutions pages say who they are for; the waitlist asks.
    const forWho = q.get("for");
    if (forWho) {
      try {
        sessionStorage.setItem("vestra-for", forWho);
      } catch {
        // private mode: the waitlist simply asks
      }
    }
    if (q.get("error")) setError(DIDNT_START);
    setHello(greeting(new Date().getHours()));
    const calm = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (calm) {
      setTyping(false);
      return;
    }
    const t = setTimeout(() => setTyping(false), 1500);
    return () => clearTimeout(t);
  }, []);

  // Back from Google: the browser's back-forward cache restores this page exactly as it was left,
  // mid "Opening Google…", so put the button back to ready.
  useEffect(() => {
    const back = (e: PageTransitionEvent) => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
  }, []);

  const start = async () => {
    setBusy(true);
    setError(null);
    const next =
      new URLSearchParams(window.location.search).get("next") ?? undefined;
    try {
      await signInWithGoogle(next);
    } catch {
      setBusy(false);
      setError(DIDNT_START);
    }
  };

  return (
    <div className="grid h-dvh grid-rows-[minmax(0,1fr)_auto] overflow-x-clip bg-fd-sand bg-fd-grain font-fd-body text-base text-fd-ink antialiased [background-size:96px_96px] fold:grid-cols-2 fold:grid-rows-1">
      <InkDefs />

      <ClayPicture
        src="/front-door/signin-team.webp"
        srcSet="/front-door/signin-team.webp 1600w, /front-door/signin-team-2400.webp 2400w"
        sizes="(max-width: 920px) 100vw, 50vw"
        alt="The Vestra team at a table on a San Francisco rooftop at golden hour, the Golden Gate Bridge behind them, Bash waving hello and one chair pulled out, empty"
        head={[0.405, 0.325]}
        seat={[0.685, 0.565]}
        say={hello}
        typing={typing}
        className="mx-2.5 mt-2.5 min-h-0 overflow-hidden max-fold:rounded-[26px] max-fold:p-2 fold:col-start-1 fold:row-start-1 fold:my-4 fold:mr-0 fold:ml-4"
      />

      <main className="relative @container grid place-items-stretch px-6 pt-6.5 pb-[calc(22px+env(safe-area-inset-bottom))] fold:col-start-2 fold:row-start-1 fold:place-items-center fold:px-[clamp(28px,5vw,72px)] fold:pt-22 fold:pb-12">
        <a
          href="/"
          aria-label="Vestra home"
          className="absolute top-0 left-6 hidden h-16 items-center gap-2.5 fold:left-[clamp(28px,5vw,72px)] fold:flex"
        >
          <img
            src="/front-door/mark.svg"
            alt=""
            className="size-7.5 rounded-[9px]"
          />
          <img src="/front-door/wordmark.svg" alt="" className="h-5 w-auto" />
        </a>

        <div className="relative mx-auto w-full max-w-[460px] motion-safe:animate-fd-rise motion-safe:[animation-delay:0.1s] fold:max-w-[500px]">
          <h1 className="relative font-fd-display text-[min(44px,11.5vw)] leading-none font-[650] tracking-[-0.045em] whitespace-nowrap fold:text-[clamp(52px,5.4vw,84px)]">
            <Pop className="-top-[.52em] -left-[.78em] w-[.62em] [--d:0.95s] max-fold:hidden" />
            <span>Come On</span>{" "}
            <span className="relative inline-block">
              In.
              <Sun className="-top-[.62em] left-[calc(100%+.08em)] w-[.86em] [--d:1.05s] max-fold:top-[-.18em] max-fold:left-[calc(100%+.14em)] max-fold:w-[.66em]" />
              <Underline className="-bottom-[.17em] -left-[6%] h-[.3em] w-[112%]" />
            </span>
          </h1>

          <p className="relative mt-3 mb-5.5 max-w-[28ch] text-base leading-normal text-pretty text-fd-ink-3 fold:mt-5.5 fold:mb-8.5 fold:text-xl">
            Your team is already here, and they kept a seat for you.
            <Spark className="-top-4 -right-12.5 w-9.5 [--d:1.25s] max-fold:hidden" />
          </p>

          <div className="relative">
            <button
              type="button"
              onClick={start}
              disabled={busy}
              className="flex h-13.5 w-full cursor-pointer items-center justify-center gap-3 rounded-full bg-fd-ink px-5.5 text-lg font-[650] tracking-[-0.005em] text-white shadow-fd-btn transition-[transform,background-color,box-shadow] duration-200 ease-fd hover:-translate-y-px hover:bg-fd-ink-hover focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-fd-ink active:scale-[0.97] disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0 fold:h-15.5"
            >
              <span className="grid size-6 flex-none place-items-center rounded-full bg-white">
                <svg viewBox="0 0 48 48" aria-hidden="true" className="size-4">
                  <path
                    fill="#FFC107"
                    d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"
                  />
                  <path
                    fill="#FF3D00"
                    d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
                  />
                </svg>
              </span>
              {busy ? "Opening Google…" : "Continue with Google"}
            </button>
            <Arrow className="-top-1 -right-27 w-24 [--d:1.4s] [transform:scaleX(-1)_rotate(-14deg)] @max-[560px]:hidden max-fold:hidden" />
            <Mug className="top-1.5 -left-22 w-12 rotate-[-9deg] [--d:1.6s] @max-[560px]:hidden max-fold:hidden" />
          </div>

          {error && (
            <p role="alert" className="mt-3 text-sm font-bold text-fd-error">
              {error}
            </p>
          )}

          <p className="mt-3.5 text-xs leading-[1.45] text-fd-ink-3 fold:mt-4.5">
            By continuing, you agree to our{" "}
            <a
              href="/policies/terms"
              className="font-bold text-fd-ink-2 underline-offset-3 hover:text-fd-ink hover:underline"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/policies/privacy"
              className="font-bold text-fd-ink-2 underline-offset-3 hover:text-fd-ink hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
